import React, { useState, useEffect } from "react";
import { Station } from "../types";
import "./StationSearch.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

interface StationSearchProps {
  onStationSelect: (station: Station) => void;
}

const StationSearch: React.FC<StationSearchProps> = ({ onStationSelect }) => {
  const [stations, setStations] = useState<Station[]>([]);
  const [filteredStations, setFilteredStations] = useState<Station[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    fetchStations();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredStations([]);
    } else {
      const filtered = stations.filter(station =>
        station.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredStations(filtered.slice(0, 10)); // Limit to 10 results
    }
  }, [searchTerm, stations]);

  const fetchStations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/stations`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setStations(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch stations");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setShowDropdown(value.trim() !== "");
  };

  const handleStationClick = (station: Station) => {
    setSearchTerm(station.name);
    setShowDropdown(false);
    onStationSelect(station);
  };

  const handleInputFocus = () => {
    if (searchTerm.trim() !== "") {
      setShowDropdown(true);
    }
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for clicks
    setTimeout(() => setShowDropdown(false), 200);
  };

  if (loading) {
    return (
      <div className="station-search">
        <div className="loading">Loading stations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="station-search">
        <div className="error">Error: {error}</div>
        <button onClick={fetchStations} className="retry-button">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="station-search">
      <div className="search-container">
        <input
          type="text"
          placeholder="Search for a CitiBike station..."
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          className="search-input"
        />

        {showDropdown && filteredStations.length > 0 && (
          <div className="dropdown">
            {filteredStations.map(station => (
              <div
                key={station.station_id}
                className="dropdown-item"
                onClick={() => handleStationClick(station)}
              >
                <div className="station-name">{station.name}</div>
                <div className="station-details">
                  Capacity: {station.capacity} • ID: {station.station_id}
                </div>
              </div>
            ))}
          </div>
        )}

        {showDropdown &&
          searchTerm.trim() !== "" &&
          filteredStations.length === 0 && (
            <div className="dropdown">
              <div className="no-results">No stations found</div>
            </div>
          )}
      </div>
    </div>
  );
};

export default StationSearch;
